import "@/index.css";
import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import MainApp from "@/pages/MainApp";
import DevToolsPanel from "@/components/DevToolsPanel";
import { registerServiceWorker, setupInstallPrompt, promptInstall, setupNetworkListeners } from "@/lib/pwa";
import { Download, X, WifiOff, Loader2 } from "lucide-react";

// Lazy load the developer verification page
const DeveloperVerifyBuyer = lazy(() => import("@/pages/DeveloperVerifyBuyer"));

const queryClient = new QueryClient();

// Register service worker on app load
registerServiceWorker();

// Smooth loading screen
function InitialLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary rounded-sm flex items-center justify-center mx-auto mb-4 animate-pulse">
          <span className="text-primary-foreground font-bold text-lg">FBC</span>
        </div>
        <p className="text-muted-foreground text-sm">Loading Flat Buyers' Connect...</p>
      </div>
    </div>
  );
}

// PWA Install Banner — minimizable pill
function PWAInstallBanner() {
  const [showInstall, setShowInstall] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('pwa-install-dismissed')) {
      setDismissed(true);
      return;
    }
    setupInstallPrompt((available) => setShowInstall(available));
  }, []);

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) setShowInstall(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowInstall(false);
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showInstall || dismissed) return null;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed top-2 left-2 z-50 bg-primary text-white px-3 py-1.5 rounded-full shadow-lg text-xs font-medium flex items-center gap-1.5 hover:bg-primary/90 transition-colors"
      >
        <Download className="h-3 w-3" /> Install App
      </button>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-white px-4 py-2 flex items-center justify-center gap-3 text-sm shadow-md">
      <span className="font-medium">Install FBC App</span>
      <span className="text-xs opacity-80 hidden sm:inline">— Quick access from your home screen</span>
      <button onClick={handleInstall} className="bg-white text-primary px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-white/90 transition-colors">
        <Download className="h-3 w-3" /> Install
      </button>
      <button onClick={() => setMinimized(true)} className="p-0.5 hover:bg-white/20 rounded transition-colors text-xs opacity-70">
        Minimize
      </button>
      <button onClick={handleDismiss} className="p-0.5 hover:bg-white/20 rounded-full transition-colors">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Offline Banner
function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const cleanup = setupNetworkListeners(
      () => setIsOffline(false),
      () => setIsOffline(true)
    );
    return cleanup;
  }, []);

  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white py-2 px-4 text-center text-sm z-50 flex items-center justify-center gap-2">
      <WifiOff className="h-4 w-4" />
      You're offline. Some features may not be available.
    </div>
  );
}

// Main App Content
function AppContent() {
  const { loading } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!loading && !hasInitialized.current) {
      hasInitialized.current = true;
      const timer = setTimeout(() => setIsReady(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Failsafe: never hold the loading screen for more than 10s, no matter what.
  useEffect(() => {
    const failsafe = setTimeout(() => setIsReady(true), 10000);
    return () => clearTimeout(failsafe);
  }, []);

  if (!isReady) {
    return <InitialLoadingScreen />;
  }

  return <MainApp />;
}

// Loading fallback for lazy routes
function LazyLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <Suspense fallback={<LazyLoadingFallback />}>
            <Routes>
              {/* Public Developer Verification Page (no auth required) */}
              <Route path="/verify-buyer/:token" element={<DeveloperVerifyBuyer />} />
              {/* Main App Routes */}
              <Route path="/*" element={<AppContent />} />
            </Routes>
          </Suspense>
          <PWAInstallBanner />
          {/* Test-account switcher: Preview environment only, never on the live portal */}
          {window.location.hostname.includes('preview.emergentagent.com') && <DevToolsPanel />}
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                border: '1px solid hsl(var(--border))',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
